import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchComplainIdData } from "../../../Redux/Slice/ShowAllTask";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import Cookies from "js-cookie";
import {
  findJuniorDevelopers,
  findSupport,
  SeniortoJuniorAssignReq,
  SupportAcceptTask,
  updateCallNo,
} from "../../../Redux/Slice/TaskMasterSlice";
import Loader from "../../../Components/Loader/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import socket from "../../../socket";
import "../../CustomerReq/InhouseRnd.css";

import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  Building2,
  Phone,
  User,
  Paperclip,
  Download,
  ExternalLink,
  Eye,
  FileText,
  UserPlus,
  UserCheck,
  Save,
  Layers,
  Copy,
  Check,
  X,
  Music,
  Printer,
  FileDown,
  PlusCircle,
  ArrowUpRight,
  CornerDownLeft,
  PlayCircle,
  PauseCircle,
  Send,
  ShieldCheck,
  Headphones,
  XCircle,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

import {
  BsFileEarmarkPdfFill,
  BsFileEarmarkImageFill,
  BsFileEarmarkTextFill,
  BsFileEarmarkWordFill,
  BsFileEarmarkExcelFill,
  BsFileEarmarkPlayFill,
  BsFileEarmarkMusicFill,
  BsFileEarmarkFill,
} from "react-icons/bs";
import ComplaintPrintExportModal from "./ComplaintPrintExportModal";

// Date formatting helpers
const formatDate = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  return isNaN(date.getTime())
    ? "--"
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const formatDateTimeUTC = (dateString) => {
  if (!dateString) return "--";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "--";

  try {
    const dateParts = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(d);

    const part = (type) => dateParts.find((p) => p.type === type)?.value || "";
    const day = part("day");
    const month = part("month");
    const year = part("year");
    const hour = part("hour");
    const minute = part("minute");
    const dayPeriod = (part("dayPeriod") || "").toUpperCase();

    return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch (e) {
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  }
};

// Company Initials and Gradient helpers
const getCompanyInitials = (str) => {
  if (!str) return "DS";
  const parts = str.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.slice(0, 2).toUpperCase();
};

const getCompanyGradient = (str) => {
  const colors = [
    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #059669 0%, #047857 100%)",
    "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Developer Avatar component with initials fallback
const DeveloperAvatar = ({ photo, name, size = "w-8 h-8" }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  const getInitials = (n) => {
    if (!n) return "DV";
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  };

  if (photo && String(photo).trim() !== "" && !hasError) {
    return (
      <img
        src={photo}
        alt={name || "Developer"}
        className={`${size} rounded-full object-cover shrink-0 border border-slate-200 shadow-xs`}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full shrink-0 border border-slate-200 bg-blue-100 text-[#112A47] flex items-center justify-center text-[11px] font-bold tracking-tight uppercase shadow-xs`}
    >
      {getInitials(name)}
    </div>
  );
};

// Searchable Developer Dropdown for Assignment Modal
const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(
    (opt) =>
      (opt.label || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (opt.Role || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = (options || []).find((opt) => String(opt._id) === String(value));

  return (
    <div className="relative w-full mb-3" ref={dropdownRef}>
      <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
        <span>{placeholder}</span>
        <span className="text-red-500 font-bold">*</span>
      </label>
      <div
        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl cursor-pointer flex justify-between items-center hover:border-[#133A69] focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs min-h-[42px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {selectedOption ? (
            <>
              <DeveloperAvatar photo={selectedOption.ProfilePhoto} name={selectedOption.label} size="w-7 h-7" />
              <div className="flex flex-col min-w-0">
                <span className="text-slate-800 font-semibold text-xs truncate">
                  {selectedOption.label}
                </span>
                {selectedOption.Role && (
                  <span className="text-slate-400 font-medium text-[10px] truncate">
                    {selectedOption.Role}
                  </span>
                )}
              </div>
              {selectedOption.isPresent && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0 ml-auto mr-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Present
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-400 text-xs">Choose junior developer...</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 text-[#133A69]" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <div className="p-2 border-b border-slate-100 bg-slate-50/80">
            <input
              type="text"
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400"
              placeholder={`Search developer by name or role...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1 divide-y divide-slate-50">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt._id}
                  className={`flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer transition-colors rounded-lg my-0.5 ${
                    String(value) === String(opt._id)
                      ? "bg-blue-50 text-[#133A69] font-semibold"
                      : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                  }`}
                  onClick={() => {
                    onChange(opt._id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <DeveloperAvatar photo={opt.ProfilePhoto} name={opt.label} size="w-8 h-8" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="truncate leading-tight font-medium text-slate-800 text-xs">
                        {opt.label}
                      </span>
                      {opt.isPresent && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Present
                        </span>
                      )}
                    </div>
                    {opt.Role && (
                      <span className="text-[10px] text-slate-400 font-normal leading-tight truncate">
                        {opt.Role}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-xs text-slate-400 text-center italic">No developers found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Copy to clipboard helper button
const CopyButton = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text || text === "-") return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!", { autoClose: 1200 });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#133A69] px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
      title={`Copy ${label}`}
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
};

// Timeline Steps Normalizer & Helper
const getTimelineStatusIcon = (category) => {
  switch (category) {
    case "created":
      return <PlusCircle size={12} />;
    case "requirement":
      return <FileText size={12} />;
    case "completed":
      return <CheckCircle2 size={12} />;
    case "senior":
      return <ArrowUpRight size={12} />;
    case "junior":
      return <CornerDownLeft size={12} />;
    case "implementation":
      return <Send size={12} />;
    case "checking_impl":
      return <ShieldCheck size={12} />;
    case "support":
      return <Headphones size={12} />;
    case "hold":
      return <PauseCircle size={12} />;
    case "running":
      return <PlayCircle size={12} />;
    case "assigned":
      return <UserCheck size={12} />;
    case "rejected":
      return <XCircle size={12} />;
    default:
      return <Clock size={12} />;
  }
};

const buildTimelineSteps = (task) => {
  if (!task) return [];

  const rawRecords = Array.isArray(task.Record) ? [...task.Record] : [];
  const taskCreatedDate = task.Date || new Date();
  const creator = task.InPutId;
  const creatorName =
    (creator && typeof creator === "object" ? creator.Name : null) ||
    "Task Creator";
  const creatorRole =
    (creator && typeof creator === "object" ? creator.Role : null) ||
    "Customer Support / Initiator";
  const creatorPhoto =
    creator && typeof creator === "object" ? creator.ProfilePhoto : null;

  const initialAssignee = task.SuportTeamId;
  const initialAssigneeName =
    initialAssignee && typeof initialAssignee === "object"
      ? initialAssignee.Name
      : null;
  const initialAssigneeRole =
    initialAssignee && typeof initialAssignee === "object"
      ? initialAssignee.Role
      : "Developer";

  const seniorDev = task.SeniorDevloperID;
  const seniorDevName =
    seniorDev && typeof seniorDev === "object" ? seniorDev.Name : null;
  const seniorDevRole =
    seniorDev && typeof seniorDev === "object"
      ? seniorDev.Role
      : "Senior Developer";

  // Check if first record is already an initial "Pending" / "Call Punched" / "Created" record
  const firstRecord = rawRecords[0];
  const isFirstRecordCreation =
    firstRecord &&
    ((firstRecord.Status &&
      /^(pending|call punched|created|task created|call logged)$/i.test(
        firstRecord.Status.trim()
      )) ||
      (!firstRecord.Status && firstRecord.DescripTione) ||
      (firstRecord.DescripTione &&
        (firstRecord.DescripTione === task.Requirement ||
          /punch|create/i.test(firstRecord.DescripTione))));

  // 2. SUBSEQUENT WORKFLOW RECORDS (Filter out requirement updates as they are displayed in top header)
  const remainingRecords = (isFirstRecordCreation
    ? rawRecords.slice(1)
    : rawRecords
  ).filter(
    (rec) =>
      !/requirement update/i.test(rec.DescripTione || "") &&
      rec.Status !== "Requirement Updated"
  );

  // Check if any subsequent record is an assignment
  const hasSubsequentAssignment = remainingRecords.some(r =>
    /assign/i.test(r.DescripTione || "") || /assign/i.test(r.Status || "")
  );

  // For Step 1 (Task Creation):
  // If the task is assigned later by a Senior Developer, Step 1 was sent to Senior Dev / In Queue!
  let creationDestination;
  if (hasSubsequentAssignment) {
    if (seniorDevName && seniorDevName !== creatorName) {
      creationDestination = {
        to: seniorDevName,
        role: seniorDevRole,
        note: "Submitted for Senior Developer Review & Assignment",
      };
    } else {
      creationDestination = {
        to: "In Queue",
        role: "Unassigned",
        note: "Awaiting Developer Assignment / Allocation",
      };
    }
  } else if (initialAssigneeName) {
    creationDestination = {
      to: initialAssigneeName,
      role: initialAssigneeRole,
      note: "Assigned for Task Execution",
    };
  } else {
    creationDestination = {
      to: "In Queue",
      role: "Unassigned",
      note: "Awaiting Developer Assignment / Allocation",
    };
  }

  // Determine creation date:
  // Must be earlier than or equal to subsequent steps
  let creationDate = taskCreatedDate;
  if (firstRecord && firstRecord.Date) {
    creationDate = firstRecord.Date;
  }
  if (remainingRecords.length > 0 && remainingRecords[0].Date) {
    const nextStepTime = new Date(remainingRecords[0].Date).getTime();
    if (new Date(creationDate).getTime() > nextStepTime) {
      if (task.Date && new Date(task.Date).getTime() <= nextStepTime) {
        creationDate = task.Date;
      } else {
        creationDate = new Date(nextStepTime - 1000 * 60 * 30);
      }
    }
  }

  const creationDesc =
    task.Requirement ||
    (firstRecord && firstRecord.DescripTione) ||
    "Task registered and logged in the system.";

  const steps = [];

  // 1. STEP 1: ALWAYS the Task Creation / Call Logged Milestone
  steps.push({
    id:
      firstRecord && isFirstRecordCreation && firstRecord._id
        ? firstRecord._id
        : "step-created-root",
    isCreationStep: true,
    stepType: "TASK_CREATED",
    status: "Task Created / Call Logged",
    statusCategory: "created",
    partyName: creatorName,
    role: creatorRole,
    profilePhoto: creatorPhoto,
    date: creationDate,
    description: creationDesc,
    from: { name: creatorName, role: creatorRole },
    to: creationDestination,
    destinationLabel: `${creationDestination.to} (${creationDestination.role})`,
  });

  remainingRecords.forEach((rec, idx) => {
    const recEmp = rec.InHouseId;
    const hasEmp = recEmp && typeof recEmp === "object" && recEmp.Name;
    const targetUser = hasEmp ? recEmp : initialAssignee || creator;

    let partyName = targetUser?.Name || "Team Member";
    let role = targetUser?.Role || "Team Member";
    let profilePhoto = targetUser?.ProfilePhoto || null;

    if (!hasEmp && rec.DescripTione) {
      const match = rec.DescripTione.match(/(?:by|from)\s+([A-Za-z\s]+?)(?:\s*\(|$)/i);
      if (match && match[1]) {
        partyName = match[1].trim();
        role = "Owner / Support Leader";
      }
    }

    const rawStatus =
      (typeof rec.Status === "string"
        ? rec.Status.replace(/\.$/, "").trim()
        : "") || "Workflow Update";
    const date = rec.Date || taskCreatedDate;
    const description = rec.DescripTione || "No remarks provided.";

    const nextRec = remainingRecords[idx + 1];
    const nextRecEmp = nextRec?.InHouseId;
    const nextEmpName =
      nextRecEmp && typeof nextRecEmp === "object" ? nextRecEmp.Name : null;
    const nextEmpRole =
      nextRecEmp && typeof nextRecEmp === "object" ? nextRecEmp.Role : null;

    let destination = null;
    let statusCategory = "general";
    let statusLabel = rawStatus;
    let actionFrom = { name: partyName, role };
    const sLower = rawStatus.toLowerCase();

    if (
      sLower.includes("requirement update") ||
      (rec.DescripTione && /requirement update/i.test(rec.DescripTione))
    ) {
      statusCategory = "requirement";
      statusLabel = "Requirement Updated";
      destination = {
        to: initialAssigneeName || "Assigned Developer",
        role: initialAssigneeRole || "Developer",
        note: "Updated requirements synchronized to task",
      };
    } else if (sLower.includes("complete") || sLower.includes("complate")) {
      statusCategory = "completed";
      statusLabel = "Task Completed";
      destination = {
        to: "Closed & Resolved",
        role: "Final Resolution",
        note: "Verified and Marked as Completed",
      };
    } else if (
      sLower.includes("junior to senior") ||
      sLower.includes("checking to senior")
    ) {
      statusCategory = "senior";
      statusLabel = "Escalated to Senior";
      destination = {
        to: seniorDevName || nextEmpName || "Senior Developer",
        role: seniorDevRole || nextEmpRole || "Senior Reviewer",
        note: "Escalated for Code Review & Senior Checking",
      };
    } else if (sLower.includes("senior to junior")) {
      statusCategory = "junior";
      statusLabel = "Returned to Junior";
      destination = {
        to: initialAssigneeName || nextEmpName || "Junior Developer",
        role: initialAssigneeRole || nextEmpRole || "Developer",
        note: "Sent Back to Junior Developer for Revision / Fixes",
      };
    } else if (
      sLower.includes("forward for implementation") ||
      sLower.includes("implementation")
    ) {
      statusCategory = "implementation";
      statusLabel = "Forward for Implementation";
      destination = {
        to: partyName || initialAssigneeName || "Implementation Team",
        role: role || "Implementation Specialist",
        note: "Forwarded for Client-Side Deployment / Setup",
      };
    } else if (sLower.includes("checking after implementation")) {
      statusCategory = "checking_impl";
      statusLabel = "Checking after Implementation";
      destination = {
        to: seniorDevName || partyName || "Quality Reviewer",
        role: seniorDevRole || "QA / Tester",
        note: "Post-Implementation Quality Verification",
      };
    } else if (
      sLower.includes("checking to support") ||
      sLower.includes("support")
    ) {
      statusCategory = "support";
      statusLabel = "Handed over to Support";
      destination = {
        to: creatorName || "Customer Support Team",
        role: creatorRole || "Support Team",
        note: "Handed over to Support for Client Confirmation & Delivery",
      };
    } else if (sLower.includes("holding") || sLower.includes("hold")) {
      statusCategory = "hold";
      statusLabel = "Holding Task";
      destination = {
        to: partyName,
        role: role,
        note: "Task Paused / On Hold (Awaiting Client / Query Response)",
      };
    } else if (sLower.includes("accept") || sLower.includes("running")) {
      statusCategory = "running";
      statusLabel = "Task In Progress";
      destination = {
        to: partyName,
        role: role,
        note: "Task Accepted and in Active Resolution",
      };
    } else if (sLower.includes("assign") || (rec.DescripTione && rec.DescripTione.toLowerCase().includes("assign"))) {
      statusCategory = "assigned";
      statusLabel = "Assigned Developer";
      const assigner = hasEmp ? recEmp : (seniorDev || creator);
      const assigneeName = initialAssigneeName || "Developer";
      const assigneeRole = initialAssigneeRole || "Junior Developer";

      actionFrom = {
        name: assigner?.Name || partyName || "Senior Developer",
        role: assigner?.Role || role || "Senior Developer",
      };
      destination = {
        to: assigneeName,
        role: assigneeRole,
        note: `Assigned to ${assigneeName} for Task Execution`,
      };
    } else if (sLower.includes("reject")) {
      statusCategory = "rejected";
      statusLabel = "Task Rejected";
      destination = {
        to: "Task Cancelled / Rejected",
        role: "Closed",
        note: "Rejected with reasons provided",
      };
    } else {
      statusCategory = "general";
      destination = {
        to: nextEmpName || partyName,
        role: nextEmpRole || role,
        note: "Workflow Stage Updated",
      };
    }

    steps.push({
      id: rec._id || `step-${idx + 1}`,
      isCreationStep: false,
      stepType: "TRANSITION",
      status: statusLabel,
      rawStatus,
      statusCategory,
      partyName,
      role,
      profilePhoto,
      date,
      description,
      from: actionFrom,
      to: destination,
      destinationLabel: `${destination.to} (${destination.role})`,
    });
  });

  return steps;
};

// Main CustomerDetails Component
const CustomerDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  let parsedData = null;
  try {
    const raw = Cookies.get("loginData");
    if (raw) parsedData = JSON.parse(raw);
  } catch (e) {
    console.error("Error parsing loginData:", e);
  }

  const loggedInUserId =
    parsedData?.data?._id || parsedData?.user?._id || parsedData?._id;

  const { ComplainIdData, loading: complainIdLoading } = useSelector(
    (state) => state.ShowAllTask
  );
  const { JuniorDevelopers, DataFindSupport, loading: juniorDevelopersLoading } = useSelector(
    (state) => state.TaskMaster
  );

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [complaintData, setComplaintData] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [assignDescription, setAssignDescription] = useState("");

  const [editedPresentStatus, setEditedPresentStatus] = useState("");
  const [editedRequirement, setEditedRequirement] = useState("");
  const [editedSolution, setEditedSolution] = useState("");
  const [editedBenefit, setEditedBenefit] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPrintExportOpen, setIsPrintExportOpen] = useState(false);

  // Allow editing if in inhouse R&D and task is still pending
  const pathLower = window.location.pathname.toLowerCase();
  const isEditable =
    (pathLower.includes("/inhousernd") || pathLower.includes("/inhousrnd")) &&
    complaintData?.status?.toLowerCase() === "pending";

  useEffect(() => {
    if (complaintData) {
      setEditedPresentStatus(complaintData.presentStatus || "");
      setEditedRequirement(complaintData.requirement || "");
      setEditedSolution(complaintData.solution || "");
      setEditedBenefit(complaintData.benefit || "");
    }
  }, [complaintData]);

  const currentCallHolder = useMemo(() => {
    const statusStr = (complaintData?.status || "").toLowerCase();
    const records = complaintData?.records || [];
    const latestRec = records.length > 0 ? records[records.length - 1] : null;

    // 1. If status is checking to senior and senior developer exists
    if (statusStr.includes("checking to senior") && complaintData?.seniorDeveloper && complaintData.seniorDeveloper !== "-") {
      return complaintData.seniorDeveloper;
    }

    // 2. If assigned member exists
    if (complaintData?.assignedMember && complaintData.assignedMember !== "Unassigned" && complaintData.assignedMember !== "-") {
      return complaintData.assignedMember;
    }

    // 3. If latest record has a party name
    if (latestRec?.partyName && latestRec.partyName !== "Team Member") {
      return latestRec.partyName;
    }

    // 4. Senior developer
    if (complaintData?.seniorDeveloper && complaintData.seniorDeveloper !== "-") {
      return complaintData.seniorDeveloper;
    }

    // 5. Creator
    if (complaintData?.employee && complaintData.employee !== "-") {
      return complaintData.employee;
    }

    return "Unassigned";
  }, [complaintData]);

  const handleSaveDetails = async () => {
    setIsSaving(true);
    try {
      const resultAction = await dispatch(
        updateCallNo({
          taskId: id,
          PresentStatus: editedPresentStatus,
          Requirement: editedRequirement,
          Solution: editedSolution,
          Benefit: editedBenefit,
        })
      );
      if (updateCallNo.fulfilled.match(resultAction)) {
        toast.success("Task details saved successfully!");
        dispatch(fetchComplainIdData(id));
      } else {
        toast.error(resultAction.payload || "Failed to update task details.");
      }
    } catch (error) {
      console.error("Error updating task details:", error);
      toast.error(error.message || "An error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsInitialLoading(true);
      try {
        if (id) {
          await dispatch(fetchComplainIdData(id));
        }
        await dispatch(findJuniorDevelopers());
        await dispatch(findSupport());
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadData();
  }, [dispatch, id]);

  useEffect(() => {
    const handleUpdate = (updatePayload) => {
      if (id && (!updatePayload?.id || String(updatePayload.id) === String(id))) {
        dispatch(fetchComplainIdData(id));
      }
    };
    socket.on("customerReqUpdate", handleUpdate);
    return () => {
      socket.off("customerReqUpdate", handleUpdate);
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (ComplainIdData && ComplainIdData?.data) {
      const apiData = ComplainIdData?.data;
      const validPhotos = (apiData?.Photos || []).filter((url) => url);

      setComplaintData({
        date: apiData.Date,
        callNo: apiData.CallNo,
        company: apiData.CompanyName?.WhaClientName || "-",
        project: apiData.Project?.project_name || "-",
        module:
          apiData.Module?.ModuleName || apiData.Module?.module_name || "-",
        type: apiData.Type?.Type_name || apiData.Type?.type_name || "-",
        presentStatus: apiData.PresentStatus || "-",
        requirement: apiData.Requirement || "-",
        solution: apiData.Solution || "-",
        benefit: apiData.Benefit || "-",
        employee: apiData?.InPutId?.Name || "-",
        assignedMember: apiData?.SuportTeamId?.Name || "Unassigned",
        seniorDeveloper: apiData?.SeniorDevloperID?.Name || "-",
        status: apiData.Status || "-",
        Partition: apiData.Partition || "-",
        ContactPerName: apiData.ContactPerName || "-",
        ContactPer: apiData.ContactPer || "-",
        codeUpdate: apiData.CodeUpdate || "-",
        lastRequirementUpdatedByName: apiData.LastRequirementUpdatedByName || (() => {
          if (Array.isArray(apiData.Record)) {
            for (let i = apiData.Record.length - 1; i >= 0; i--) {
              const r = apiData.Record[i];
              if (/Requirement updated/i.test(r.DescripTione || '') || r.Status === 'Requirement Updated') {
                const match = (r.DescripTione || '').match(/(?:by|from)\s+([A-Za-z\s]+?)(?:\s*\(|$)/i);
                return (match && match[1]) ? match[1].trim() : (r.InHouseId?.Name || 'Gopesh Periwal');
              }
            }
          }
          return '';
        })(),
        lastRequirementUpdatedAt: apiData.LastRequirementUpdatedAt || (() => {
          if (Array.isArray(apiData.Record)) {
            for (let i = apiData.Record.length - 1; i >= 0; i--) {
              const r = apiData.Record[i];
              if (/Requirement updated/i.test(r.DescripTione || '') || r.Status === 'Requirement Updated') {
                return r.Date || null;
              }
            }
          }
          return null;
        })(),
        photos: validPhotos.map((url, idx) => ({
          id: `photo-${idx}`,
          title: `Attachment ${idx + 1}`,
          file: url,
        })),
        records: buildTimelineSteps(apiData),
      });
    } else {
      setComplaintData(null);
    }
  }, [ComplainIdData]);

  const openPhotoModal = (photo) => setSelectedPhoto(photo);
  const closePhotoModal = () => setSelectedPhoto(null);

  const detectExtension = (url, mimeType) => {
    const cleanUrl = (url || "").split("?")[0].toLowerCase();
    const cleanMime = (mimeType || "").toLowerCase();

    // Excel
    if (
      cleanUrl.includes("vnd.ms-excel") ||
      cleanUrl.endsWith(".xls") ||
      cleanMime.includes("vnd.ms-excel") ||
      cleanMime.includes("excel")
    )
      return "xls";
    if (
      cleanUrl.includes("spreadsheetml") ||
      cleanUrl.endsWith(".xlsx") ||
      cleanMime.includes("spreadsheetml")
    )
      return "xlsx";
    // Word
    if (
      cleanUrl.includes("wordprocessingml") ||
      cleanUrl.endsWith(".docx") ||
      cleanMime.includes("wordprocessingml")
    )
      return "docx";
    if (
      cleanUrl.includes("msword") ||
      cleanUrl.endsWith(".doc") ||
      cleanMime.includes("msword")
    )
      return "doc";
    // PowerPoint
    if (
      cleanUrl.includes("presentationml") ||
      cleanUrl.endsWith(".pptx") ||
      cleanMime.includes("presentationml")
    )
      return "pptx";
    if (
      cleanUrl.includes("ms-powerpoint") ||
      cleanUrl.endsWith(".ppt") ||
      cleanMime.includes("ms-powerpoint")
    )
      return "ppt";
    // PDF
    if (cleanUrl.endsWith(".pdf") || cleanMime.includes("pdf")) return "pdf";
    // CSV / Text
    if (cleanUrl.endsWith(".csv") || cleanMime.includes("csv")) return "csv";
    if (
      cleanUrl.endsWith(".txt") ||
      cleanUrl.endsWith(".plain") ||
      cleanMime.includes("text/plain")
    )
      return "txt";
    // Images
    if (cleanUrl.endsWith(".png") || cleanMime.includes("image/png")) return "png";
    if (
      cleanUrl.endsWith(".jpg") ||
      cleanUrl.endsWith(".jpeg") ||
      cleanMime.includes("image/jpeg")
    )
      return "jpg";
    if (cleanUrl.endsWith(".webp") || cleanMime.includes("image/webp")) return "webp";
    if (cleanUrl.endsWith(".gif") || cleanMime.includes("image/gif")) return "gif";
    if (cleanUrl.endsWith(".svg") || cleanMime.includes("svg")) return "svg";
    if (cleanUrl.endsWith(".bmp") || cleanMime.includes("bmp")) return "bmp";
    // Media
    if (cleanUrl.endsWith(".mp4") || cleanMime.includes("video/mp4")) return "mp4";
    if (cleanUrl.endsWith(".webm") || cleanMime.includes("video/webm")) return "webm";
    if (cleanUrl.endsWith(".mp3") || cleanMime.includes("audio/mpeg")) return "mp3";
    if (cleanUrl.endsWith(".wav") || cleanMime.includes("audio/wav")) return "wav";
    if (cleanUrl.endsWith(".zip") || cleanMime.includes("zip")) return "zip";

    const parts = cleanUrl.split("/");
    const last = parts.pop();
    if (last && last.includes(".")) {
      const ext = last.split(".").pop();
      if (ext && ext.length >= 2 && ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) {
        return ext;
      }
    }
    return "bin";
  };

  const getFileType = (file) => {
    if (!file) return "unknown";

    if (typeof file === "string" && file.startsWith("data:")) {
      const mime = file.split(";")[0].replace("data:", "").toLowerCase();
      if (mime.startsWith("image/")) return "image";
      if (mime.includes("pdf")) return "pdf";
      if (
        mime.includes("word") ||
        mime.includes("wordprocessingml") ||
        mime.includes("officedocument.wordprocessingml")
      )
        return "word";
      if (
        mime.includes("excel") ||
        mime.includes("spreadsheetml") ||
        mime.includes("vnd.ms-excel")
      )
        return "excel";
      if (mime.includes("csv")) return "csv";
      if (mime.includes("text") || mime.includes("plain")) return "text";
      if (mime.startsWith("video/")) return "video";
      if (mime.startsWith("audio/")) return "audio";
      return "file";
    }

    if (typeof file === "string") {
      const ext = detectExtension(file);
      if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext)) return "image";
      if (ext === "pdf") return "pdf";
      if (["xls", "xlsx"].includes(ext)) return "excel";
      if (["doc", "docx"].includes(ext)) return "word";
      if (["ppt", "pptx"].includes(ext)) return "powerpoint";
      if (ext === "csv") return "csv";
      if (ext === "txt") return "text";
      if (["mp4", "webm", "ogg", "mkv", "avi"].includes(ext)) return "video";
      if (["mp3", "wav", "m4a", "aac"].includes(ext)) return "audio";
      return "file";
    }

    return "unknown";
  };

  const getFileIconComponent = (type) => {
    switch (type) {
      case "pdf":
        return BsFileEarmarkPdfFill;
      case "image":
        return BsFileEarmarkImageFill;
      case "word":
        return BsFileEarmarkWordFill;
      case "excel":
      case "csv":
        return BsFileEarmarkExcelFill;
      case "text":
        return BsFileEarmarkTextFill;
      case "video":
        return BsFileEarmarkPlayFill;
      case "audio":
        return BsFileEarmarkMusicFill;
      default:
        return BsFileEarmarkFill;
    }
  };

  const getFileColorClass = (type) => {
    switch (type) {
      case "pdf":
        return { text: "text-red-500", bg: "bg-red-50", border: "border-red-200" };
      case "image":
        return { text: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" };
      case "word":
        return { text: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" };
      case "excel":
        return { text: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
      case "csv":
        return { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" };
      case "text":
        return { text: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" };
      case "video":
        return { text: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" };
      case "audio":
        return { text: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" };
      default:
        return { text: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" };
    }
  };

  const getRecordStatusBadgeClass = (statusStr) => {
    if (!statusStr) return "bg-slate-100 text-slate-700 border-slate-200";
    const s = String(statusStr).toLowerCase().trim();
    if (s.includes("create") || s === "created" || s.includes("punched")) {
      return "bg-sky-50 text-sky-700 border-sky-300";
    }
    if (s.includes("complete") || s === "completed") {
      return "bg-emerald-50 text-emerald-700 border-emerald-300";
    }
    if (s.includes("accept") || s.includes("running")) {
      return "bg-blue-50 text-blue-700 border-blue-300";
    }
    if (s.includes("assign") || s === "assigned") {
      return "bg-blue-50 text-blue-700 border-blue-300";
    }
    if (s.includes("senior")) {
      return "bg-purple-50 text-purple-700 border-purple-300";
    }
    if (s.includes("junior")) {
      return "bg-orange-50 text-orange-700 border-orange-300";
    }
    if (s.includes("support")) {
      return "bg-cyan-50 text-cyan-700 border-cyan-300";
    }
    if (s.includes("forward") || s.includes("impl") || s.includes("implementation")) {
      return "bg-indigo-50 text-indigo-700 border-indigo-300";
    }
    if (s.includes("hold")) {
      return "bg-amber-50 text-amber-700 border-amber-300";
    }
    if (s.includes("reject") || s.includes("rejected")) {
      return "bg-rose-50 text-rose-700 border-rose-300";
    }
    if (s.includes("solve") || s.includes("dynamic")) {
      return "bg-teal-50 text-teal-700 border-teal-300";
    }
    if (s.includes("pending")) {
      return "bg-amber-50 text-amber-700 border-amber-300";
    }
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const memberList = useMemo(() => {
    const map = new Map();
    const seenNames = new Set();

    const addMember = (m, defaultRole) => {
      if (!m || !m._id) return;
      if (loggedInUserId && String(m._id) === String(loggedInUserId)) return;

      // Filter out inactive / profile off members
      if (m.Profile && String(m.Profile).toLowerCase() === "off") return;
      if (m.profile && String(m.profile).toLowerCase() === "off") return;
      if (m.Status && String(m.Status).toLowerCase() === "off") return;
      if (m.status && String(m.status).toLowerCase() === "off") return;
      if (m.isActive === false || m.active === false) return;

      const name = (m.Name || "").trim();
      const nameLower = name.toLowerCase();
      if (!name || nameLower.includes("dynamic management")) return;

      const role = (m.Role || defaultRole || "").trim();
      const roleLower = role.toLowerCase();
      if (roleLower.includes("owner") || roleLower.includes("management") || roleLower.includes("support")) return;

      const nameKey = name.toLowerCase();
      const idKey = String(m._id);

      if (!map.has(idKey) && !seenNames.has(nameKey)) {
        seenNames.add(nameKey);
        map.set(idKey, {
          label: name,
          _id: m._id,
          ProfilePhoto: m.ProfilePhoto || null,
          Role: role || defaultRole,
          isPresent: Boolean(m.isPresent),
          punchInTime: m.punchInTime || null,
        });
      }
    };

    if (Array.isArray(JuniorDevelopers?.data)) {
      JuniorDevelopers.data.forEach((d) => addMember(d, "Developer"));
    }

    return Array.from(map.values()).sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
  }, [JuniorDevelopers?.data, loggedInUserId]);

  const handleDownload = async (fileUrl, fileName) => {
    if (!fileUrl) return;
    try {
      toast.info("Downloading attachment...", { autoClose: 1500 });
      const fetchUrl =
        fileUrl + (fileUrl.includes("?") ? "&" : "?") + "bypassConfig=true&time=" + Date.now();

      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      const blob = await response.blob();
      const detectedExt = detectExtension(
        fileUrl,
        blob.type || response.headers.get("content-type")
      );

      let baseName = (fileName || "attachment").replace(/\.[a-zA-Z0-9]+$/, "");
      const safeFileName = `${baseName}.${detectedExt}`;

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = safeFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success(`Downloaded ${safeFileName} successfully!`);
    } catch (error) {
      console.error("Direct download failed, utilizing fallback:", error);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      const ext = detectExtension(fileUrl);
      let baseName = (fileName || "attachment").replace(/\.[a-zA-Z0-9]+$/, "");
      link.download = `${baseName}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAcceptTask = () => {
    const userId = parsedData?.data?._id || parsedData?.user?._id || parsedData?._id;
    if (!userId) {
      toast.error("User not found. Please login again.");
      return;
    }

    toast.info("Accepting task...", { autoClose: 1500 });
    dispatch(SupportAcceptTask({ id: userId, tid: id }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Task accepted successfully!");
          dispatch(fetchComplainIdData(id));
        } else {
          toast.error("Failed to accept task. Please try again.");
        }
      })
      .catch((err) => {
        toast.error("An error occurred while accepting task.");
      });
  };

  if (
    isInitialLoading ||
    complainIdLoading ||
    juniorDevelopersLoading ||
    !complaintData
  ) {
    return <Loader />;
  }

  const userRole = (parsedData?.data?.Role || parsedData?.user?.Role || "").toLowerCase();
  const isInhouseRndRoute =
    window.location.pathname.toLowerCase().includes("/inhousernd") ||
    window.location.pathname.toLowerCase().includes("/inhousrnd");
  const isSenior =
    isInhouseRndRoute ||
    [
      "delphi senior developer",
      "web senior developer",
      "owner",
      "senior customer support",
      "senior developer",
      "admin",
    ].some((r) => userRole.includes(r)) ||
    userRole.includes("senior") ||
    userRole.includes("owner") ||
    userRole.includes("lead");

  // Calculate duration in days
  const calculateDays = () => {
    if (!complaintData?.date) return "0 Days";
    const start = new Date(complaintData.date);
    let end = new Date();
    if (complaintData?.records?.length > 0) {
      end = new Date(complaintData.records[complaintData.records.length - 1].date);
    }
    const diffTime = Math.abs(end - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const finalDays = diffDays === 0 ? 1 : diffDays;
    const st = complaintData?.status?.toLowerCase();
    if (st === "completed") return `${finalDays} Days (Completed)`;
    if (st === "accept") return `${finalDays} Days (Accepted)`;
    return `${finalDays} Days`;
  };

  const statusLower = (complaintData?.status || "Pending").toLowerCase();

  return (
    <div className="w-full pb-10">
      {/* ===================== Top Header Banner ===================== */}
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-[#112A47] via-[#133A69] to-[#1E4E8C] text-white p-4 sm:p-5 md:p-6 shadow-md border border-[#0d284a] relative overflow-hidden">
        {/* Subtle decorative background circles */}
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/5 pointer-events-none"></div>
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/5 pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Left Block: Back Button, Title, Subtitle, Badges */}
          <div className="flex flex-col gap-3 min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 active:bg-white/30 text-white text-xs font-semibold backdrop-blur-sm border border-white/20 transition-all shadow-xs"
                title="Go back to previous page"
              >
                <ArrowLeft size={14} />
                <span>Back</span>
              </button>

              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-200 font-medium">Ticket:</span>
                <span className="px-3 py-1 rounded-lg text-xs font-bold tracking-wide shadow-xs bg-amber-400 text-amber-950 border border-amber-300">
                  #{complaintData?.callNo || id}
                </span>
              </div>

              {/* Status Badge */}
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide border shadow-xs ${
                  statusLower.includes("complete")
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/30"
                    : statusLower.includes("accept") || statusLower.includes("running")
                    ? "bg-blue-500/20 text-blue-200 border-blue-400/30"
                    : statusLower.includes("hold")
                    ? "bg-purple-500/20 text-purple-200 border-purple-400/30"
                    : statusLower.includes("reject")
                    ? "bg-rose-500/20 text-rose-200 border-rose-400/30"
                    : "bg-amber-500/20 text-amber-200 border-amber-400/30"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    statusLower.includes("complete")
                      ? "bg-emerald-400 animate-pulse"
                      : statusLower.includes("accept") || statusLower.includes("running")
                      ? "bg-blue-400 animate-pulse"
                      : "bg-amber-400 animate-pulse"
                  }`}
                ></span>
                <span className="capitalize">{complaintData?.status || "Pending"}</span>
              </span>
            </div>

            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-white mb-1">
                {complaintData?.company && complaintData.company !== "-"
                  ? complaintData.company
                  : "Customer Requirement Details"}
              </h1>
              <p className="text-xs text-blue-200/90 font-medium flex items-center gap-2 flex-wrap">
                <span>Call #{complaintData?.callNo || id}</span>
                <span>•</span>
                <span>Created: {formatDate(complaintData?.date)}</span>
                <span>•</span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} className="text-blue-300" />
                  <span>Duration: {calculateDays()}</span>
                </span>
                <span>•</span>
                <span className="inline-flex items-center gap-1">
                  <User size={11} className="text-blue-300" />
                  <span>Assigned: {complaintData?.assignedMember || "Unassigned"}</span>
                </span>
              </p>
            </div>
          </div>

          {/* Right Block: Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0 w-full md:w-auto justify-start md:justify-end pt-2 md:pt-0 border-t md:border-t-0 border-white/10">
            {/* Print / Export Report Button */}
            <button
              type="button"
              onClick={() => setIsPrintExportOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-white/15 hover:bg-white/25 active:bg-white/30 backdrop-blur-sm border border-white/25 transition-all shadow-xs"
              title="Choose Report Type to Print / Export"
            >
              <Printer size={14} />
              <span>Print / Export</span>
            </button>

            {isSenior ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsAssignModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 transition-all shadow-sm border border-indigo-400/30 flex-1 md:flex-initial"
                >
                  <UserPlus size={14} />
                  <span>Assign Request</span>
                </button>

                {statusLower === "pending" && (
                  <button
                    type="button"
                    onClick={handleAcceptTask}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-all shadow-sm border border-emerald-400/30 flex-1 md:flex-initial"
                  >
                    <CheckCircle2 size={14} />
                    <span>Accept Task</span>
                  </button>
                )}
              </>
            ) : (
              statusLower === "pending" && (
                <button
                  type="button"
                  onClick={handleAcceptTask}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition-all shadow-sm border border-emerald-400/30 flex-1 md:flex-initial"
                >
                  <CheckCircle2 size={14} />
                  <span>Accept Task</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* ===================== Top Section: 12-Column Responsive Grid ===================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start mb-6">
        {/* Left Column (8 cols): Requirement, Scope & Ticket Info */}
        <div className="lg:col-span-8 flex flex-col gap-5">
          {/* Card: Requirement & Task Scope */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200/80 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2.5">
                <span className="p-1.5 rounded-lg bg-[#133A69] text-white">
                  <FileText size={15} />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 leading-tight">
                    Requirement & Task Scope
                  </h3>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Detailed task specifications, client requirements, and solution overview
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {complaintData?.lastRequirementUpdatedByName && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200/90 text-[11px] text-amber-900 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                    <Clock size={12} className="text-amber-600 shrink-0" />
                    <span>
                      Last updated by <strong className="font-semibold text-amber-950">{complaintData.lastRequirementUpdatedByName}</strong>
                      {complaintData.lastRequirementUpdatedAt ? ` on ${formatDateTimeUTC(complaintData.lastRequirementUpdatedAt)}` : ""}
                    </span>
                  </div>
                )}
                {isEditable && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                    Editable Mode
                  </span>
                )}
              </div>
            </div>

            <div className="p-5">
              {isEditable ? (
                <div className="flex flex-col gap-4">
                  {/* Editable: Present Status */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        <span>Present Status</span>
                      </label>
                      <CopyButton text={editedPresentStatus} label="Present Status" />
                    </div>
                    <textarea
                      rows={3}
                      value={editedPresentStatus}
                      onChange={(e) => setEditedPresentStatus(e.target.value)}
                      placeholder="Describe current system behavior or existing module status..."
                      className="w-full px-3.5 py-2.5 text-xs text-slate-800 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-y leading-relaxed"
                    />
                  </div>

                  {/* Editable: Requirement */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                        <span>Requirement</span>
                        <span className="text-red-500">*</span>
                      </label>
                      <CopyButton text={editedRequirement} label="Requirement" />
                    </div>
                    <textarea
                      rows={4}
                      value={editedRequirement}
                      onChange={(e) => setEditedRequirement(e.target.value)}
                      placeholder="Specify the exact client requirement, expected changes, or feature request..."
                      className="w-full px-3.5 py-2.5 text-xs text-slate-800 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-y leading-relaxed"
                    />
                  </div>

                  {/* Editable: Solution */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        <span>Proposed Solution</span>
                      </label>
                      <CopyButton text={editedSolution} label="Solution" />
                    </div>
                    <textarea
                      rows={3}
                      value={editedSolution}
                      onChange={(e) => setEditedSolution(e.target.value)}
                      placeholder="Technical resolution plan, code architecture, or database modifications..."
                      className="w-full px-3.5 py-2.5 text-xs text-slate-800 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-y leading-relaxed"
                    />
                  </div>

                  {/* Editable: Benefit */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        <span>Client Benefits</span>
                      </label>
                      <CopyButton text={editedBenefit} label="Benefits" />
                    </div>
                    <textarea
                      rows={3}
                      value={editedBenefit}
                      onChange={(e) => setEditedBenefit(e.target.value)}
                      placeholder="Impact on performance, reporting, workflow ease, or client satisfaction..."
                      className="w-full px-3.5 py-2.5 text-xs text-slate-800 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-y leading-relaxed"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={handleSaveDetails}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#112A47] hover:bg-[#0c2545] active:bg-[#081a30] transition-all shadow-sm"
                    >
                      <Save size={14} />
                      <span>{isSaving ? "Saving Changes..." : "Save Task Details"}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Read-only: Present Status */}
                  <div className="rounded-xl border border-slate-200/90 bg-amber-50/30 p-4 relative border-l-4 border-l-amber-500">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                        <span>Present Status</span>
                      </span>
                      <CopyButton text={complaintData?.presentStatus} label="Present Status" />
                    </div>
                    <p className="text-xs text-slate-800 font-medium leading-relaxed whitespace-pre-wrap break-words">
                      {complaintData?.presentStatus && complaintData.presentStatus !== "-"
                        ? complaintData.presentStatus
                        : "No present status recorded."}
                    </p>
                  </div>

                  {/* Read-only: Requirement */}
                  <div className="rounded-xl border border-slate-200/90 bg-blue-50/30 p-4 relative border-l-4 border-l-blue-600">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1.5">
                        <span>Requirement</span>
                      </span>
                      <CopyButton text={complaintData?.requirement} label="Requirement" />
                    </div>
                    <p className="text-xs text-slate-800 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                      {complaintData?.requirement && complaintData.requirement !== "-"
                        ? complaintData.requirement
                        : "No requirement specified."}
                    </p>
                  </div>

                  {/* Read-only: Solution */}
                  <div className="rounded-xl border border-slate-200/90 bg-emerald-50/30 p-4 relative border-l-4 border-l-emerald-500">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                        <span>Solution</span>
                      </span>
                      <CopyButton text={complaintData?.solution} label="Solution" />
                    </div>
                    <p className="text-xs text-slate-800 font-medium leading-relaxed whitespace-pre-wrap break-words">
                      {complaintData?.solution && complaintData.solution !== "-"
                        ? complaintData.solution
                        : "No solution documented yet."}
                    </p>
                  </div>

                  {/* Read-only: Benefits */}
                  <div className="rounded-xl border border-slate-200/90 bg-purple-50/30 p-4 relative border-l-4 border-l-purple-500">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-purple-900 flex items-center gap-1.5">
                        <span>Benefits</span>
                      </span>
                      <CopyButton text={complaintData?.benefit} label="Benefits" />
                    </div>
                    <p className="text-xs text-slate-800 font-medium leading-relaxed whitespace-pre-wrap break-words">
                      {complaintData?.benefit && complaintData.benefit !== "-"
                        ? complaintData.benefit
                        : "No benefits recorded."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Card: Ticket Information Details Grid */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200/80 flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-blue-600 text-white">
                <Layers size={14} />
              </span>
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                System & Workflow Parameters
              </h3>
            </div>

            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Token / Call No</span>
                <span className="text-xs font-bold text-[#133A69] mt-0.5 truncate">#{complaintData?.callNo || "--"}</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Created Date</span>
                <span className="text-xs font-semibold text-slate-800 mt-0.5 truncate">{formatDate(complaintData?.date)}</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Type</span>
                <span className="text-xs font-semibold text-slate-800 mt-0.5 truncate">{complaintData?.type || "--"}</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Project</span>
                <span className="text-xs font-semibold text-slate-800 mt-0.5 truncate" title={complaintData?.project}>{complaintData?.project || "--"}</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Module</span>
                <span className="text-xs font-semibold text-slate-800 mt-0.5 truncate" title={complaintData?.module}>{complaintData?.module || "--"}</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex flex-col">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Posted By</span>
                <span className="text-xs font-semibold text-slate-800 mt-0.5 truncate">{complaintData?.Partition || "--"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (4 cols): Client Details & Attachments */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          {/* Card: Client & Contact Person */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-200/80 flex items-center gap-2">
              <span className="p-1 rounded-md bg-emerald-600 text-white">
                <Building2 size={14} />
              </span>
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Client & Contact Details
              </h3>
            </div>

            <div className="p-4 space-y-3.5">
              {/* Company banner */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200/80">
                <span
                  className="w-10 h-10 rounded-xl font-bold text-white flex items-center justify-center text-xs shrink-0 shadow-xs"
                  style={{
                    background: getCompanyGradient(complaintData?.company),
                  }}
                >
                  {getCompanyInitials(complaintData?.company)}
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="text-xs font-bold text-slate-800 truncate" title={complaintData?.company}>
                    {complaintData?.company || "--"}
                  </h4>
                  <p className="text-[10px] text-slate-400 font-semibold truncate">
                    Registered Client
                  </p>
                </div>
              </div>

              {/* Contact info items */}
              <div className="divide-y divide-slate-100">
                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium flex items-center gap-1.5">
                    <Clock size={12} className="text-slate-400" />
                    <span>Current Status</span>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold border shadow-2xs ${
                      statusLower.includes("complete")
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : statusLower.includes("accept") || statusLower.includes("running")
                        ? "bg-blue-50 text-blue-700 border-blue-300"
                        : statusLower.includes("hold")
                        ? "bg-purple-50 text-purple-700 border-purple-300"
                        : statusLower.includes("reject")
                        ? "bg-rose-50 text-rose-700 border-rose-300"
                        : "bg-amber-50 text-amber-700 border-amber-300"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        statusLower.includes("complete")
                          ? "bg-emerald-500 animate-pulse"
                          : statusLower.includes("accept") || statusLower.includes("running")
                          ? "bg-blue-500 animate-pulse"
                          : "bg-amber-500 animate-pulse"
                      }`}
                    ></span>
                    <span className="capitalize">{complaintData?.status || "Pending"}</span>
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium flex items-center gap-1.5">
                    <UserCheck size={12} className="text-blue-600" />
                    <span>Current Call Holder</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-bold text-[#133A69] bg-blue-50/80 px-2.5 py-0.5 rounded-lg border border-blue-200/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                    <span className="truncate max-w-[140px] sm:max-w-[180px]">{currentCallHolder}</span>
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium flex items-center gap-1.5">
                    <User size={12} className="text-slate-400" />
                    <span>Contact Person</span>
                  </span>
                  <span className="font-semibold text-slate-800">
                    {complaintData?.ContactPerName || "--"}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium flex items-center gap-1.5">
                    <Phone size={12} className="text-slate-400" />
                    <span>Phone Number</span>
                  </span>
                  {complaintData?.ContactPer && complaintData.ContactPer !== "-" ? (
                    <a
                      href={`tel:${complaintData.ContactPer}`}
                      className="font-bold text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                    >
                      <span>{complaintData.ContactPer}</span>
                    </a>
                  ) : (
                    <span className="text-slate-400">--</span>
                  )}
                </div>

                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium">Create Task By</span>
                  <span className="font-semibold text-slate-800">
                    {complaintData?.employee || "--"}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="text-slate-400 font-medium">Assigned Developer</span>
                  <span className="font-semibold text-[#133A69]">
                    {complaintData?.assignedMember || "Unassigned"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Attachments & Files */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-200/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="p-1 rounded-md bg-purple-600 text-white">
                  <Paperclip size={14} />
                </span>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Attachments ({complaintData?.photos?.length || 0})
                </h3>
              </div>
            </div>

            <div className="p-4">
              {complaintData?.photos?.length > 0 ? (
                <div className="flex flex-col gap-2.5 max-h-[340px] overflow-y-auto pr-1">
                  {complaintData.photos.map((photo) => {
                    const fileType = getFileType(photo.file);
                    const colorClass = getFileColorClass(fileType);
                    const IconComp = getFileIconComponent(fileType);
                    const ext = detectExtension(photo.file);

                    return (
                      <div
                        key={photo.id}
                        className="flex items-center justify-between p-2.5 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 rounded-xl transition-all cursor-pointer group"
                        onClick={() => openPhotoModal(photo)}
                      >
                        <div className="flex items-center gap-2.5 overflow-hidden flex-1 min-w-0 mr-2">
                          <div
                            className={`p-2 rounded-lg ${colorClass.bg} ${colorClass.text} shrink-0`}
                          >
                            <IconComp className="text-base" />
                          </div>
                          <div className="overflow-hidden min-w-0">
                            <p className="text-xs font-bold text-slate-700 truncate group-hover:text-[#133A69] transition-colors">
                              {photo.title}.{ext}
                            </p>
                            <p className="text-[10px] text-slate-400 capitalize">
                              {fileType === "excel"
                                ? "Excel Spreadsheet"
                                : fileType === "word"
                                ? "Word Document"
                                : `${fileType} file`}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPhotoModal(photo);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-100/70 rounded-lg transition-colors"
                            title="Preview file"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(photo.file, photo.title);
                            }}
                            className="p-1.5 text-slate-600 hover:text-[#133A69] hover:bg-slate-200/70 rounded-lg transition-colors"
                            title="Download file"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                  <Paperclip size={24} className="mx-auto text-slate-300 mb-1.5" />
                  <p className="text-xs font-semibold text-slate-600">No attachments</p>
                  <p className="text-[10px] text-slate-400">
                    No files or screenshots uploaded for this call
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===================== Task Resolution Timeline (Alternating Zig-Zag View) ===================== */}
      <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden mb-6">
        {/* Timeline Header */}
        <div className="bg-gradient-to-r from-[#112A47] via-[#133A69] to-[#1E4E8C] text-white p-4 sm:p-5 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Clock className="text-blue-300" size={18} />
              <h2 className="text-base sm:text-lg font-bold tracking-wide text-white">
                Task Resolution Timeline
              </h2>
            </div>
            <p className="text-blue-200 text-xs font-medium ml-6">
              Cross-lined escalation workflow & alternating step resolution history
            </p>
          </div>
          {complaintData?.records?.length > 0 && (
            <span className="bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-bold px-3 py-1 rounded-full shadow-xs">
              {complaintData.records.length} {complaintData.records.length === 1 ? "Step" : "Steps"}
            </span>
          )}
        </div>

        {/* Timeline Body with alternating zig-zag steps */}
        <div className="p-4 sm:p-6 md:p-8 bg-white">
          {complaintData?.records?.length > 0 ? (
            <div className="relative py-3">
              {/* Central Vertical Guide Line: Crisp, visible, continuous vertical line connecting all steps */}
              {/* Desktop Center Line */}
              <div
                className="hidden md:block absolute"
                style={{
                  position: "absolute",
                  top: "14px",
                  bottom: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "3px",
                  backgroundColor: "#cbd5e1",
                  zIndex: 0,
                  borderRadius: "9999px",
                }}
              />
              {/* Mobile Left Line */}
              <div
                className="md:hidden absolute"
                style={{
                  position: "absolute",
                  top: "14px",
                  bottom: "20px",
                  left: "20px",
                  transform: "translateX(-50%)",
                  width: "3px",
                  backgroundColor: "#cbd5e1",
                  zIndex: 0,
                  borderRadius: "9999px",
                }}
              />

              {complaintData.records.map((rec, index) => {
                const isEven = index % 2 === 0;

                // Color palette matching the previous colorful design
                const colorThemes = [
                  {
                    border: "border-emerald-500",
                    stepBg: "bg-emerald-600",
                    dotColor: "border-emerald-500",
                    commentBg: "bg-emerald-50/70",
                  },
                  {
                    border: "border-blue-500",
                    stepBg: "bg-blue-600",
                    dotColor: "border-blue-500",
                    commentBg: "bg-blue-50/70",
                  },
                  {
                    border: "border-amber-500",
                    stepBg: "bg-amber-500",
                    dotColor: "border-amber-500",
                    commentBg: "bg-amber-50/70",
                  },
                  {
                    border: "border-purple-500",
                    stepBg: "bg-purple-600",
                    dotColor: "border-purple-500",
                    commentBg: "bg-purple-50/70",
                  },
                  {
                    border: "border-rose-500",
                    stepBg: "bg-rose-500",
                    dotColor: "border-rose-500",
                    commentBg: "bg-rose-50/70",
                  },
                  {
                    border: "border-teal-500",
                    stepBg: "bg-teal-600",
                    dotColor: "border-teal-500",
                    commentBg: "bg-teal-50/70",
                  },
                  {
                    border: "border-indigo-500",
                    stepBg: "bg-indigo-600",
                    dotColor: "border-indigo-500",
                    commentBg: "bg-indigo-50/70",
                  },
                ];

                const color = colorThemes[index % colorThemes.length];

                return (
                  <div
                    key={rec.id || index}
                    className={`mb-7 flex justify-between items-start w-full relative group ${
                      isEven ? "md:flex-row-reverse" : "md:flex-row"
                    }`}
                  >
                    {/* Empty opposite-side spacer on desktop */}
                    <div className="hidden md:block w-[calc(50%-32px)]"></div>

                    {/* Timeline Center Dot */}
                    <div className="absolute left-[20px] md:left-1/2 top-2 z-10 -translate-x-1/2 flex items-center justify-center">
                      <div
                        className={`w-4 h-4 rounded-full bg-white border-4 ${color.dotColor} shadow-xs ring-4 ring-slate-100 transition-transform duration-300 group-hover:scale-125`}
                      ></div>
                    </div>

                    {/* Connecting Horizontal Line */}
                    {/* Mobile: from left line to card */}
                    <div
                      className="absolute md:hidden z-0"
                      style={{
                        position: "absolute",
                        left: "20px",
                        top: "14px",
                        width: "26px",
                        height: "2px",
                        backgroundColor: "#cbd5e1",
                        zIndex: 1,
                      }}
                    />

                    {/* Desktop: from center line to card */}
                    <div
                      className="hidden md:block absolute z-0"
                      style={{
                        position: "absolute",
                        top: "14px",
                        width: "32px",
                        height: "2px",
                        backgroundColor: "#cbd5e1",
                        left: isEven ? "calc(50% - 32px)" : "50%",
                        zIndex: 1,
                      }}
                    />

                    {/* Step Card Container */}
                    <div className="w-full pl-12 md:pl-0 md:w-[calc(50%-32px)] flex flex-col">
                      {/* Step Number Badge & Status Pill: Inner edge facing the center timeline line */}
                      <div
                        className={`mb-2 flex items-center gap-2 flex-wrap ${
                          isEven ? "md:flex-row-reverse md:justify-start" : "md:justify-start"
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center ${color.stepBg} text-[11px] px-3.5 py-1 rounded-full font-bold text-white shadow-xs whitespace-nowrap tracking-wide`}
                        >
                          Step {index + 1}
                        </span>
                        {rec.status && (
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-bold border shadow-2xs ${getRecordStatusBadgeClass(
                              rec.statusCategory || rec.status
                            )}`}
                          >
                            {getTimelineStatusIcon(rec.statusCategory)}
                            <span>{rec.status}</span>
                          </span>
                        )}
                      </div>

                      {/* Card with left-accent border */}
                      <div
                        className={`${color.commentBg} border border-l-[6px] rounded-2xl ${color.border} shadow-xs hover:shadow-md p-4 sm:p-5 transition-all duration-300 relative`}
                      >
                        {/* Header: Developer Avatar, Name, Role & Date */}
                        <div className="flex flex-wrap items-center justify-between gap-2.5 pb-3 mb-3 border-b border-slate-200/70">
                          <div className="flex items-center gap-3 min-w-0">
                            <DeveloperAvatar
                              photo={rec.profilePhoto}
                              name={rec.partyName}
                              size="w-9 h-9 sm:w-10 sm:h-10"
                            />
                            <div className="min-w-0 flex-1">
                              <h4 className="font-bold text-slate-800 text-xs sm:text-sm leading-tight truncate">
                                {rec.partyName}
                              </h4>
                              <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">
                                {rec.Role || rec.role || "Team Member"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 font-semibold px-2.5 py-1 rounded-lg bg-white/90 border border-slate-200 shadow-2xs">
                              <Clock size={12} className="text-blue-600 shrink-0" />
                              <span>{formatDateTimeUTC(rec.date)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Route / Flow Direction Box (Kaha timeline gaya) */}
                        <div className="bg-white/95 border border-slate-200/90 rounded-xl p-3 mb-3 shadow-2xs">
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            <div className="inline-flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200">
                              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                Action By:
                              </span>
                              <span className="font-bold text-slate-800">{rec.from?.name}</span>
                              {rec.from?.role && (
                                <span className="text-[10px] text-slate-500 font-medium">
                                  ({rec.from.role})
                                </span>
                              )}
                            </div>

                            <div className="inline-flex items-center gap-1 text-blue-600 font-bold px-1">
                              <span className="text-sm">➔</span>
                              <span className="text-[10px] uppercase tracking-wider font-extrabold text-blue-700">
                                Handed Over To:
                              </span>
                            </div>

                            <div className="inline-flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200">
                              <span className="font-bold text-slate-800">{rec.to?.to}</span>
                              {rec.to?.role && (
                                <span className="text-[10px] text-slate-500 font-medium">
                                  ({rec.to.role})
                                </span>
                              )}
                            </div>
                          </div>
                          {rec.to?.note && (
                            <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed border-slate-200 text-[11px] text-slate-600 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                              <span>{rec.to.note}</span>
                            </div>
                          )}
                        </div>

                        {/* Inner White Box: Status, Requirement & Work Description */}
                        <div className="bg-white rounded-xl p-3.5 sm:p-4 shadow-2xs border border-slate-100">
                          <p className="text-[10px] uppercase tracking-wider font-extrabold text-slate-400 mb-1">
                            {rec.isCreationStep
                              ? "Initial Requirement / Issue Details:"
                              : "Resolution Details & Remarks:"}
                          </p>
                          <div className="text-xs sm:text-[13px] text-slate-800 leading-relaxed font-normal whitespace-pre-wrap break-words">
                            {rec.description || "No description provided."}
                          </div>

                          {rec.statusCategory === "completed" && complaintData?.codeUpdate && complaintData?.codeUpdate !== "-" && (
                            <div className="mt-2.5 pt-2.5 border-t border-dashed border-slate-200">
                              <p className="text-[10px] uppercase tracking-wider font-extrabold text-slate-400 mb-1">
                                Code / Solution Reference:
                              </p>
                              <div className="text-xs font-mono bg-slate-50 p-2 rounded-lg text-slate-700 border border-slate-200">
                                {complaintData.codeUpdate}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 px-4 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
              <Clock size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-xs font-semibold text-slate-600 mb-0.5">
                No timeline records available yet.
              </p>
              <p className="text-[11px] text-slate-400">
                Action notes and handovers will appear here as developers work on this task.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ===================== File Preview Modal ===================== */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[99999] p-3 sm:p-5"
          onClick={closePhotoModal}
        >
          <div
            className="relative w-full max-w-4xl max-h-[92vh] overflow-hidden bg-white rounded-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-slate-100 bg-slate-50/90">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-2 bg-blue-100 text-[#133A69] rounded-lg shrink-0">
                  {(() => {
                    const fileType = getFileType(selectedPhoto.file);
                    const IconComp = getFileIconComponent(fileType);
                    return <IconComp className="text-lg" />;
                  })()}
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                    {selectedPhoto.title}
                  </h3>
                  <p className="text-[10px] text-slate-400 capitalize">
                    {getFileType(selectedPhoto.file)} document preview
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={selectedPhoto.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-700 bg-white hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 font-semibold shadow-xs transition-colors"
                >
                  <ExternalLink size={13} />
                  <span className="hidden sm:inline">Open in Tab</span>
                </a>

                <button
                  type="button"
                  onClick={() => handleDownload(selectedPhoto.file, selectedPhoto.title)}
                  className="inline-flex items-center gap-1.5 text-xs text-white bg-[#133A69] hover:bg-[#0f2e54] px-3 py-1.5 rounded-lg font-semibold shadow-xs transition-colors"
                >
                  <Download size={13} />
                  <span className="hidden sm:inline">Download</span>
                </button>

                <button
                  type="button"
                  onClick={closePhotoModal}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-auto bg-slate-100 flex-1 flex items-center justify-center min-h-[50vh]">
              {(() => {
                const fileType = getFileType(selectedPhoto.file);

                if (fileType === "image") {
                  return (
                    <TransformWrapper
                      initialScale={1}
                      minScale={0.5}
                      maxScale={8}
                      centerOnInit={true}
                    >
                      <TransformComponent>
                        <img
                          src={selectedPhoto.file}
                          alt={selectedPhoto.title}
                          className="max-h-[70vh] max-w-full mx-auto rounded-lg shadow-md object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14L6 17h12l-3.86-5.14z'/%3E%3C/svg%3E";
                          }}
                        />
                      </TransformComponent>
                    </TransformWrapper>
                  );
                }

                if (fileType === "pdf") {
                  return (
                    <iframe
                      src={selectedPhoto.file}
                      className="w-[85vw] max-w-3xl h-[70vh] rounded-xl border-0 shadow-lg bg-white"
                      title="PDF Preview"
                    />
                  );
                }

                if (fileType === "video") {
                  return (
                    <video
                      controls
                      src={selectedPhoto.file}
                      className="max-h-[70vh] max-w-full rounded-xl shadow-lg"
                    />
                  );
                }

                if (fileType === "audio") {
                  return (
                    <div className="bg-white rounded-2xl p-8 shadow-xl w-full max-w-md text-center border border-slate-100">
                      <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4">
                        <Music size={28} />
                      </div>
                      <h4 className="font-bold text-sm text-slate-800 mb-4">
                        {selectedPhoto.title}
                      </h4>
                      <audio controls src={selectedPhoto.file} className="w-full" />
                    </div>
                  );
                }

                // Documents: Word / Excel / PowerPoint / Text
                return (
                  <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full border border-slate-100">
                    <div
                      className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xs ${
                        getFileColorClass(fileType).bg
                      } ${getFileColorClass(fileType).text}`}
                    >
                      {(() => {
                        const IconComp = getFileIconComponent(fileType);
                        return <IconComp className="text-4xl" />;
                      })()}
                    </div>
                    <h3 className="text-base font-bold text-slate-800 break-words px-2">
                      {selectedPhoto.title}
                    </h3>
                    <span className="inline-block mt-2 px-3 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-full uppercase tracking-wider">
                      {fileType} Document
                    </span>
                    <p className="text-slate-500 text-xs mt-3 mb-6">
                      Download this document or open directly in your local application.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => handleDownload(selectedPhoto.file, selectedPhoto.title)}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#133A69] hover:bg-[#0f2e54] text-white px-5 py-2.5 rounded-xl font-bold shadow-xs text-xs transition-colors"
                      >
                        <Download size={13} />
                        <span>Download File</span>
                      </button>
                      <a
                        href={selectedPhoto.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 hover:bg-slate-200 px-5 py-2.5 rounded-xl font-bold border border-slate-200 text-xs transition-colors"
                      >
                        <ExternalLink size={13} />
                        <span>Open Directly</span>
                      </a>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ===================== Assign Request Modal ===================== */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-100 animate-in fade-in zoom-in-95 duration-100">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <span className="p-2 rounded-xl bg-blue-100 text-[#133A69]">
                  <UserPlus size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Assign Request</h3>
                  <p className="text-[11px] text-slate-500">
                    Delegate this requirement to a developer
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setSelectedMemberId("");
                  setAssignDescription("");
                }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <SearchableDropdown
              options={memberList}
              value={selectedMemberId}
              onChange={(id) => setSelectedMemberId(id)}
              placeholder="Select Junior Developer"
            />

            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Instructions / Description
              </label>
              <textarea
                value={assignDescription}
                onChange={(e) => setAssignDescription(e.target.value)}
                placeholder="Add instructions, tasks, or guidelines for the developer..."
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:bg-white focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-none"
                rows={4}
              ></textarea>
            </div>

            <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-bold rounded-xl transition-colors"
                onClick={() => {
                  setIsAssignModalOpen(false);
                  setSelectedMemberId("");
                  setAssignDescription("");
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className={`px-5 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-all shadow-xs ${
                  !selectedMemberId
                    ? "bg-slate-300 cursor-not-allowed text-white"
                    : "bg-[#133A69] hover:bg-[#0f2e54] text-white"
                }`}
                disabled={!selectedMemberId}
                onClick={() => {
                  const seniorId =
                    parsedData?.data?._id || parsedData?.user?._id || parsedData?._id;
                  dispatch(
                    SeniortoJuniorAssignReq({
                      complainBoxId: id,
                      SuportTeamId: selectedMemberId,
                      SupportTeamId: selectedMemberId,
                      DescripTione: assignDescription,
                      SeniorDevloperID: seniorId,
                    })
                  )
                    .then((res) => {
                      if (res.meta.requestStatus === "fulfilled") {
                        toast.success("Request assigned successfully!");
                        dispatch(fetchComplainIdData(id));
                      } else {
                        toast.error("Failed to assign request. Please try again.");
                      }
                    })
                    .catch((error) => {
                      console.error("Assignment error:", error);
                      toast.error("An error occurred. Please try again.");
                    });
                  setIsAssignModalOpen(false);
                  setSelectedMemberId("");
                  setAssignDescription("");
                }}
              >
                <UserPlus size={13} />
                <span>Assign Task</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ===================== Complaint Print & Export Modal ===================== */}
      {isPrintExportOpen && (
        <ComplaintPrintExportModal
          isOpen={isPrintExportOpen}
          onClose={() => setIsPrintExportOpen(false)}
          complaint={complaintData || ComplainIdData?.data}
        />
      )}
    </div>
  );
};

export default CustomerDetails;
